/**
 * ZApiKeyConnections - Zod Validation Schemas for API Key Connections API
 *
 * Single source of truth for API Key Connections domain API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * API Key Connections are for services like Telegram, Twilio, Slack (non-OAuth), etc.
 * where authentication uses static tokens/secrets instead of OAuth flow.
 *
 * Naming: Z prefix = Zod schemas (vs TApi = TypeScript types, DbEntity = Prisma)
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import type { ApiKeyConnectionSettings } from "../../../schemas/ApiKeyConnection";
import { NonEmptyString } from "../../../shared/core/non-empty-string";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS - Reusable pieces
// ═══════════════════════════════════════════════════════════════

/**
 * API Key Service enum schema
 */
export const ApiKeyServiceSchema = z.nativeEnum(DbEnum.ApiKeyService);

/**
 * Connection ID validation (UUID)
 */
const ConnectionIdSchema = z.string().uuid("Invalid connection ID format");

/**
 * Connection name validation, for a name that IS supplied.
 *
 * A blank is refused rather than silently renaming the record — and that now
 * includes WHITESPACE. The previous spelling was a bare `.min(1)`, which counts
 * characters: it refused `""` and accepted `" "`, so a space-only name passed
 * the contract and was only caught downstream by
 * `resolveApiKeyConnectionName`. `NonEmptyString` trims first, so the contract
 * and the value object now agree, and both agree with the CHECK constraint on
 * `ApiKeyConnection.name`.
 *
 * OMITTING the field is the legal way to say "derive it": every create path
 * makes the key `.optional()`, and the server then takes the name the service
 * validator fetched (a Telegram bot's display name, a Slack team name).
 */
const ConnectionNameSchema = NonEmptyString("Connection name").max(
  255,
  "Connection name must be 255 characters or less"
);

/**
 * Connection status enum
 */
const ConnectionStatusSchema = z.enum(["CONNECTED", "FAILED", "EXPIRED"]);

/**
 * Linked deployment info
 */
const LinkedDeploymentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(DbEnum.DeploymentType),
  isActive: z.boolean()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS - Input validation
// ═══════════════════════════════════════════════════════════════

/**
 * LIST - Query parameters for listing connections
 */
export const ApiKeyConnectionListParamsSchema = z.object({
  service: ApiKeyServiceSchema.optional()
});

/**
 * CREATE - Request body for creating a connection
 */
export const ApiKeyConnectionCreateBodySchema = z.object({
  /** Optional pre-generated ID (for Slack manual setup with pre-configured webhook URL) */
  id: z.string().uuid().optional(),
  service: ApiKeyServiceSchema,
  /** Display name. Omit it to let the service validator supply one (see ConnectionNameSchema). */
  name: ConnectionNameSchema.optional(),
  description: z.string().optional(),
  /** Service-specific credentials (botToken, authToken, etc.) */
  credentials: z.record(z.string(), z.string()),
  /** Parent connection ID (for child connections like FB_MESSENGER_PAGE -> TWILIO) */
  apiKeyConnectionId: z.string().uuid().optional(),
  /** Service-specific settings (facebookPageId, pageName, etc.) — used by child connections */
  settings: z.record(z.string(), z.unknown()).optional()
});

/**
 * DELETE - Path variables
 */
export const ApiKeyConnectionDeletePathVarsSchema = z.object({
  id: ConnectionIdSchema
});

/**
 * UPDATE_SETTINGS - Path variables
 */
export const ApiKeyConnectionUpdateSettingsPathVarsSchema = z.object({
  id: ConnectionIdSchema
});

/**
 * UPDATE_SETTINGS - Request body
 */
export const ApiKeyConnectionUpdateSettingsBodySchema = z.object({
  /** Service-specific settings to update (partial update) */
  settings: z.record(z.string(), z.unknown())
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS - Output validation
// ═══════════════════════════════════════════════════════════════

/**
 * Full API Key Connection response
 */
export const ApiKeyConnectionResponseSchema = z.object({
  id: z.string().uuid(),
  service: ApiKeyServiceSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  status: ConnectionStatusSchema,
  failureReason: z.string().nullable(),
  /** Service-specific settings (bot info, webhook status, etc.) */
  settings: z.custom<ApiKeyConnectionSettings>().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastUsedAt: z.string().datetime().nullable(),
  lastValidatedAt: z.string().datetime().nullable(),
  deploymentsCount: z.number().int().min(0),
  /** Linked deployments (for exclusivity check) */
  linkedDeployments: z.array(LinkedDeploymentSchema).optional()
});

/**
 * LIST - Response with connections array
 */
export const ApiKeyConnectionListResponseSchema = z.object({
  connections: z.array(ApiKeyConnectionResponseSchema)
});

/**
 * CREATE - Response with created connection summary
 */
export const ApiKeyConnectionCreateResponseSchema = z.object({
  id: z.string().uuid(),
  service: ApiKeyServiceSchema,
  name: z.string(),
  status: z.string(),
  createdAt: z.string().datetime()
});

// ═══════════════════════════════════════════════════════════════
// ZAPI_KEY_CONNECTIONS MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZApiKeyConnections - Zod API validation schemas
 *
 * Single source of truth for API Key Connections API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * Base path: /api/deployments/api-key-connections
 */
export const ZApiKeyConnections = {
  List: {
    method: "GET" as const,
    path: "/api/deployments/api-key-connections",
    Params: ApiKeyConnectionListParamsSchema,
    Response: ApiSuccessSchema(ApiKeyConnectionListResponseSchema)
  },
  Create: {
    method: "POST" as const,
    path: "/api/deployments/api-key-connections",
    Body: ApiKeyConnectionCreateBodySchema,
    Response: ApiSuccessSchema(ApiKeyConnectionCreateResponseSchema)
  },
  Delete: {
    method: "DELETE" as const,
    path: "/api/deployments/api-key-connections/:id",
    PathVars: ApiKeyConnectionDeletePathVarsSchema,
    Response: ApiSuccessSchema(z.void())
  },
  UploadPhoto: {
    method: "POST" as const,
    path: "/api/deployments/api-key-connections/:id/photo",
    PathVars: z.object({ id: z.string().uuid() }),
    // File upload - handled by FormData, no Body schema needed
    Response: ApiSuccessSchema(
      z.object({
        success: z.boolean(),
        photoUrl: z.string().url()
      })
    )
  },
  UpdateSettings: {
    method: "PATCH" as const,
    path: "/api/deployments/api-key-connections/:id/settings",
    PathVars: ApiKeyConnectionUpdateSettingsPathVarsSchema,
    Body: ApiKeyConnectionUpdateSettingsBodySchema,
    Response: ApiSuccessSchema(ApiKeyConnectionResponseSchema)
  }
} as const;
